import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  Loader2,
  PenTool,
  Printer,
  RefreshCw,
  Ruler,
  Upload,
  Wrench,
  X,
} from "lucide-react";
import Reveal from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CONFIG = {
  materials: [
    { id: "pla", name: "PLA Standard" },
    { id: "pla_carbone", name: "PLA Carbone" },
    { id: "petg", name: "PETG Résistant" },
  ],
  colors: [
    { id: "black", name: "Noir Carbone (Mat)", hex: "#18181B" },
    { id: "white", name: "Blanc Craie Pur", hex: "#F4F4F5" },
    { id: "grey", name: "Gris Béton Technique", hex: "#71717A" },
    { id: "orange", name: "Orange Atelier Signature", hex: "#EA580C" },
    { id: "terracotta", name: "Terre Cuite Minérale", hex: "#C2410C" },
    { id: "blue", name: "Bleu Prusse CAO", hex: "#1E40AF" },
  ],
};

const PRINT_VOLUME_NOTE =
  "Volume max d'impression : 220 × 220 × 220 mm par pièce — au-delà, l'objet est réalisé en plusieurs parties assemblées pour atteindre les dimensions finales.";

const StepLabel = ({ n, children }) => (
  <div className="mb-4 flex items-center gap-3">
    <span className="font-mono text-[11px] tracking-[0.2em] text-accent">{n}</span>
    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ink">{children}</span>
    <span className="h-px flex-1 bg-black/10" />
  </div>
);

const inputCls =
  "w-full rounded-xl border border-accent/70 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition-colors placeholder:font-normal placeholder:text-neutral-400 focus:border-accent focus:ring-2 focus:ring-accent/20";

export default function QuoteEstimatorForm() {
  const [projectType, setProjectType] = useState("creation");
  const [description, setDescription] = useState("");
  const [usage, setUsage] = useState("");
  const [remark, setRemark] = useState("");
  const [noDims, setNoDims] = useState(false);
  const [L, setL] = useState("");
  const [W, setW] = useState("");
  const [H, setH] = useState("");
  const [wantPrint, setWantPrint] = useState(false);
  const [material, setMaterial] = useState("pla");
  const [color, setColor] = useState("black");
  const [quantity, setQuantity] = useState(1);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/config`).then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const hasDims = useMemo(
    () => !noDims && +L > 0 && +W > 0 && +H > 0,
    [noDims, L, W, H]
  );

  const onFiles = async (e) => {
    const room = 4 - photos.length;
    const files = Array.from(e.target.files || []).slice(0, room);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await axios.post(`${API}/upload`, fd);
        setPhotos((p) => [
          ...p,
          {
            path: data.path,
            preview: f.type && f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
            name: f.name,
          },
        ]);
      } catch {
        toast.error(`Envoi impossible : ${f.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (ev) => {
    ev.preventDefault();
    if (sending) return;
    setSending(true);
    const payload = {
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      project_type: projectType,
      description: description.trim(),
      usage: usage.trim() || null,
      remark: remark.trim() || null,
      has_dimensions: hasDims,
      length_mm: hasDims ? +L : null,
      width_mm: hasDims ? +W : null,
      height_mm: hasDims ? +H : null,
      want_print: wantPrint,
      material,
      color,
      quantity,
      photo_paths: photos.map((p) => p.path),
    };
    try {
      const { data } = await axios.post(`${API}/quote`, payload);
      setResult({ ...data, clientName: name.trim(), clientEmail: email.trim(), hadDims: hasDims });
      toast.success(`Demande envoyée — réf. ${data.reference}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible — vérifiez les champs puis réessayez.");
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setResult(null);
    setDescription("");
    setUsage("");
    setRemark("");
    setPhotos([]);
  };

  return (
    <section id="devis" className="scroll-mt-20 py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <Reveal>
          <div className="mb-10 flex flex-col items-start gap-4 rounded-[20px] border border-accent/30 bg-orange-50 px-6 py-5 sm:flex-row sm:items-center">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" />
            </span>
            <p className="text-sm leading-relaxed text-neutral-800">
              <strong className="font-semibold">Lancement officiel des commandes : octobre 2026.</strong>{" "}
              Pré-réservez votre créneau de modélisation dès maintenant pour faire partie des
              premiers projets prioritaires.
            </p>
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          {result ? (
            <motion.div
              key="success"
              data-testid="quote-success-panel"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="corner-card relative mx-auto max-w-2xl rounded-[20px] border border-black/10 bg-white p-8 text-center sm:p-12"
            >
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
              <h2 className="mt-5 font-display text-2xl font-extrabold tracking-tight text-ink">
                Demande envoyée. Merci {result.clientName.split(" ")[0]} !
              </h2>
              <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-neutral-500">
                Référence — <span className="text-accent">{result.reference}</span>
              </p>
              <p className="mt-5 text-sm leading-relaxed text-neutral-600">
                {result.hadDims
                  ? "Votre demande de devis a bien été transmise à l'atelier avec vos cotes."
                  : "Votre demande de contact standard a bien été transmise — nous définirons ensemble les dimensions et le chiffrage."}
              </p>
              <p className="mt-3 text-sm text-neutral-600">
                Réponse sous 48 h ouvrées à l'adresse {result.clientEmail}.
              </p>
              <button
                data-testid="new-request-button"
                onClick={reset}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-black/15 px-6 py-3 text-sm font-semibold text-ink transition-colors hover:border-ink"
              >
                <RefreshCw className="h-4 w-4" /> Faire une nouvelle demande
              </button>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              data-testid="quote-estimator-form"
              onSubmit={submit}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl space-y-10"
            >
              <div>
                <StepLabel n="01 —">Type de demande</StepLabel>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    { id: "creation", icon: PenTool, title: "Création d'un nouvel objet", sub: "À partir d'une idée, d'un croquis ou d'un besoin" },
                    { id: "repair", icon: Wrench, title: "Pièce de réparation / remplacement", sub: "Une pièce cassée à remodéliser à l'identique" },
                  ].map(({ id, icon: Icon, title, sub }) => (
                    <button
                      key={id}
                      type="button"
                      data-testid={`project-type-${id}-button`}
                      onClick={() => setProjectType(id)}
                      className={`rounded-2xl border p-5 text-left transition-all duration-200 ${
                        projectType === id
                          ? "border-ink bg-ink text-white"
                          : "border-accent/70 bg-white text-ink hover:bg-orange-50/60"
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${projectType === id ? "text-accent" : "text-neutral-400"}`} />
                      <span className="mt-3 block text-sm font-semibold">{title}</span>
                      <span className={`mt-1 block text-xs leading-relaxed ${projectType === id ? "text-neutral-300" : "text-neutral-500"}`}>
                        {sub}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <StepLabel n="02 —">Description & utilité</StepLabel>
                <textarea
                  data-testid="client-description-input"
                  required
                  minLength={5}
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Décrivez la conception que vous souhaitez : forme, fonction, contraintes…"
                  className={inputCls}
                />
                <input
                  data-testid="client-usage-input"
                  type="text"
                  value={usage}
                  onChange={(e) => setUsage(e.target.value)}
                  placeholder="Utilité de l'objet (ex. pièce d'usure d'un portail, adaptateur de vélo…)"
                  className={`${inputCls} mt-3`}
                />
                <p className="mb-1.5 mt-5 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                  Remarque — optionnel
                </p>
                <textarea
                  data-testid="client-remark-input"
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="Donnez encore plus de détails : dimensions précises, contraintes de montage, matière souhaitée, épaisseur, couleur…"
                  className={inputCls}
                />
              </div>

              <div>
                <StepLabel n="03 —">Photos ou croquis (image ou PDF)</StepLabel>
                <label
                  data-testid="repair-photo-dropzone"
                  className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-accent/70 bg-white px-6 py-8 text-center transition-colors hover:bg-orange-50/60"
                >
                  <Upload className="h-5 w-5 text-accent" />
                  <span className="text-sm font-medium text-ink">Joindre des fichiers</span>
                  <span className="text-xs text-neutral-500">JPG, PNG, WEBP ou PDF — 4 fichiers max, 10 Mo par fichier</span>
                  <span className="flex items-center gap-1.5 text-xs text-neutral-500">
                    <Ruler className="h-3.5 w-3.5 text-accent" />
                    {projectType === "repair"
                      ? "Photo de la pièce cassée sous 3 angles avec une règle à côté, si possible"
                      : "Un croquis, même simple, ou des photos d'inspiration accélèrent l'étude"}
                  </span>
                  <input
                    ref={fileRef}
                    data-testid="repair-photo-upload-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    multiple
                    className="sr-only"
                    onChange={onFiles}
                    disabled={uploading || photos.length >= 4}
                  />
                </label>
                {(photos.length > 0 || uploading) && (
                  <div className="mt-3 flex flex-wrap gap-3">
                    {photos.map((p, i) => (
                      <div key={p.path} className="relative h-20 w-20 overflow-hidden rounded-xl border border-accent/50">
                        {p.preview ? (
                          <img src={p.preview} alt={p.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full flex-col items-center justify-center gap-1 bg-orange-50/60 px-1">
                            <FileText className="h-5 w-5 text-accent" />
                            <span className="w-full truncate text-center font-mono text-[9px] text-neutral-500">{p.name}</span>
                          </div>
                        )}
                        <button
                          type="button"
                          data-testid={`photo-remove-${i}`}
                          onClick={() => removePhoto(i)}
                          className="absolute right-1 top-1 rounded-full bg-ink/80 p-1 text-white transition-colors hover:bg-accent"
                          aria-label={`Retirer ${p.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                    {uploading && (
                      <div className="flex h-20 w-20 items-center justify-center rounded-xl border border-dashed border-accent/70">
                        <Loader2 className="h-5 w-5 animate-spin text-accent" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <StepLabel n="04 —">Cotes connues de l'objet</StepLabel>
                <div className={`grid grid-cols-3 gap-3 transition-opacity ${noDims ? "pointer-events-none opacity-40" : ""}`}>
                  {[
                    { label: "Longueur", value: L, set: setL, id: "dimension-length-input" },
                    { label: "Largeur", value: W, set: setW, id: "dimension-width-input" },
                    { label: "Hauteur", value: H, set: setH, id: "dimension-height-input" },
                  ].map(({ label, value, set, id }) => (
                    <label key={id} className="block">
                      <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                        {label}
                      </span>
                      <span className="relative block">
                        <input
                          data-testid={id}
                          type="number"
                          min="1"
                          max="2000"
                          value={value}
                          onChange={(e) => set(e.target.value)}
                          placeholder="0"
                          className={inputCls}
                        />
                        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-xs text-neutral-400">
                          mm
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="no-dimensions-toggle"
                  onClick={() => setNoDims((v) => !v)}
                  className={`mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                    noDims ? "border-ink bg-ink text-white" : "border-accent/70 bg-white text-neutral-700 hover:bg-orange-50/60"
                  }`}
                >
                  <Ruler className="h-3.5 w-3.5" />
                  {noDims ? "Cotes masquées — demande de contact standard" : "Je n'ai pas les cotes exactes"}
                </button>
                <AnimatePresence>
                  {noDims && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden pt-3 text-xs leading-relaxed text-neutral-600"
                    >
                      Aucun problème : votre demande partira comme une demande de contact standard,
                      et le chiffrage sera établi sur mesure après un échange gratuit.
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <StepLabel n="05 —">Option impression 3D</StepLabel>
                <div className="flex items-center justify-between gap-4 rounded-2xl border border-accent/70 bg-white p-5">
                  <div className="flex items-center gap-3">
                    <Printer className="h-5 w-5 text-accent" />
                    <div>
                      <p className="text-sm font-semibold text-ink">Faire imprimer ma pièce</p>
                      <p className="text-xs text-neutral-500">Sinon, vous recevez uniquement les fichiers 3D</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="option-3d-print-toggle"
                    checked={wantPrint}
                    onCheckedChange={setWantPrint}
                    className="data-[state=checked]:bg-accent"
                  />
                </div>

                <AnimatePresence>
                  {wantPrint && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-5">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Matière</p>
                        <div className="flex flex-wrap gap-2">
                          {cfg.materials.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              data-testid={`material-option-${m.id}`}
                              onClick={() => setMaterial(m.id)}
                              className={`rounded-full border px-4 py-2 text-xs font-medium transition-colors ${
                                material === m.id ? "border-ink bg-ink text-white" : "border-accent/70 bg-white text-neutral-700 hover:bg-orange-50/60"
                              }`}
                            >
                              {m.name}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="pt-5">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Couleur du filament</p>
                        <div className="flex flex-wrap items-center gap-3">
                          {cfg.colors.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              data-testid={`filament-color-option-${c.id}`}
                              title={c.name}
                              aria-label={c.name}
                              onClick={() => setColor(c.id)}
                              style={{ backgroundColor: c.hex }}
                              className={`h-9 w-9 rounded-full border transition-transform duration-150 hover:scale-110 ${
                                color === c.id ? "ring-2 ring-ink ring-offset-2" : "ring-1 ring-black/10"
                              } ${c.id === "white" ? "border-black/20" : "border-transparent"}`}
                            />
                          ))}
                          <button
                            type="button"
                            data-testid="filament-color-option-custom"
                            title="Autre couleur — délai majoré d'une semaine"
                            aria-label="Autre couleur"
                            onClick={() => setColor("custom")}
                            className={`flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-accent/70 bg-white text-accent transition-all duration-150 hover:scale-110 ${
                              color === "custom" ? "ring-2 ring-accent ring-offset-2" : ""
                            }`}
                          >
                            <span className="text-lg leading-none">+</span>
                          </button>
                          <span className="ml-1 text-xs text-neutral-500" data-testid="filament-color-label">
                            {color === "custom" ? "Autre couleur — délai +1 semaine" : cfg.colors.find((c) => c.id === color)?.name}
                          </span>
                        </div>
                      </div>
                      <div className="pt-5">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-neutral-500">Quantité</p>
                        <input
                          data-testid="quantity-input"
                          type="number"
                          min="1"
                          max="50"
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, Math.min(50, +e.target.value || 1)))}
                          className="w-24 rounded-xl border border-accent/70 bg-white px-4 py-3 text-sm font-medium text-ink outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
                        />
                      </div>
                      <p className="mt-5 flex items-start gap-2 rounded-xl bg-orange-50 px-4 py-3 text-xs leading-relaxed text-neutral-700" data-testid="print-volume-note">
                        <Ruler className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                        {PRINT_VOLUME_NOTE}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <div>
                <StepLabel n="06 —">Vos coordonnées</StepLabel>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    data-testid="client-name-input"
                    required
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nom / prénom *"
                    className={inputCls}
                  />
                  <input
                    data-testid="client-email-input"
                    required
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email *"
                    className={inputCls}
                  />
                  <input
                    data-testid="client-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Téléphone (optionnel)"
                    className={`${inputCls} sm:col-span-2`}
                  />
                </div>
              </div>

              <div className="border-t border-black/10 pt-6">
                <button
                  type="submit"
                  data-testid={hasDims ? "quote-form-submit-button" : "standard-contact-submit-button"}
                  disabled={sending}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                >
                  {sending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Envoi en cours…
                    </>
                  ) : (
                    <>
                      {hasDims ? "Envoyer ma demande de devis" : "Envoyer ma demande de contact"}
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
                <p className="mt-3 text-xs leading-relaxed text-neutral-500">
                  Réponse sous 48 h ouvrées avec un chiffrage détaillé. Vos coordonnées servent
                  uniquement à l'établissement du devis — jamais transmises à des tiers.
                </p>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
